<template>
  <div class="list-approval-page">
    <KTCodePreview v-bind:title="'Danh sách phiếu yêu cầu duyệt chi'">
      <template v-slot:toolbar>
        <div class="row">
          <div class="col-md-5">
            <b-dropdown
              size="sm"
              id="dropdown-1"
              right
            >
              <template slot="button-content">
                <i class="fas fa-cog"></i>
                <span class="font-weight-bolder">Thao tác</span>
              </template>
              <b-dropdown-item @click="exportToExcel">
                <span>
                  <i class="far fa-file-excel"></i>
                  &nbsp; Xuất Excel
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </template>
      <template v-slot:preview>
        <approval-list-filter
          :type-options="listType"
          :status-options="listStatus"
          :payment-type-options="listPaymentType"
          :initial-filter="initialFilterData"
          @filter="handleFilter"
          ref="filterComponent"
        />

        <approval-list-table
          :items="listItem"
          :loading="onLoading"
          @view-item="editItem"
          @edit-item="editItem"
          @approve-item="showApproveModal"
          @change-status="showChangeStatusModal"
          @return-request="showReturnWarning"
          @delete-item="showDeleteWarning"
          @open-file-modal="openFileModal"
        />

        <approval-list-pagination
          :total-items="totalItems"
          :total-pages="totalPages"
          :current-page="page"
          @page-change="handlePageChange"
        />

        <ApproveModal
          ref="popup-approve"
          v-on:submit-data-success="fetchData"
        />
        <ApprovalChangeStatus
          ref="approval-change-status"
          v-on:submit-data-success="fetchData"
        />
        <b-modal
          ref="file-modal"
          size="lg"
          hide-footer
          title="Chỉnh sửa tệp"
          id="file-modal"
        >
          <b-row>
            <b-col cols="12">
              <upload-file
                :model-id="entityUploadId"
                :model-name="'approvals'"
                :max-files="10"
                :max-file-size="5 * 1024 * 1024"
              />
            </b-col>
          </b-row>
        </b-modal>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { cloneDeep, makeToastFaile, makeToastSuccess } from '@/utils/common';
import {
  APPROVAL_PAYMENT_TYPE,
  APPROVAL_STATUS,
  APPROVAL_TYPE,
} from '@/utils/constants';
import KTCodePreview from '@/view/content/CodePreview.vue';
import UploadFile from '@/view/modules/upload-v2/Upload.vue';
import moment from 'moment';
import Swal from 'sweetalert2';

const ApprovalListFilter = () =>
  import(/* webpackPrefetch: true */ './components/ApprovalListFilter.vue');
const ApprovalListTable = () =>
  import(/* webpackPrefetch: true */ './components/ApprovalListTable.vue');
const ApprovalListPagination = () =>
  import(/* webpackPrefetch: true */ './components/ApprovalListPagination.vue');
const ApproveModal = () =>
  import(/* webpackPrefetch: true */ './components/ApproveModal.vue');
const ApprovalChangeStatus = () =>
  import(/* webpackPrefetch: true */ './components/ApprovalChangeStatus.vue');

import { approvalRouter } from '../router';
import ApprovalService from './services/ApprovalService';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import commonMixin from '@/view/mixins/api-common.mixins.js';

export default {
  mixins: [commonMixin],
  components: {
    KTCodePreview,
    ApprovalListFilter,
    ApprovalListTable,
    ApprovalListPagination,
    ApproveModal,
    ApprovalChangeStatus,
    UploadFile,
  },
  data() {
    return {
      onLoading: false,

      // Loading states for different data types
      loadingStates: {
        stores: false,
        employees: false,
        approvals: false,
        initialization: false,
      },

      listItem: [],
      totalPages: 1,
      totalItems: 0,
      page: 1,

      listStatus: APPROVAL_STATUS,
      listType: APPROVAL_TYPE,
      listPaymentType: APPROVAL_PAYMENT_TYPE,

      entityUploadId: null,

      initialFilterData: {
        startDate: '',
        endDate: '',
        type: null,
        status: null,
        storeId: null,
        storeText: '',
        code: '',
        entityId: '',
        employeeId: null,
        employeeText: '',
        paymentType: null,
      },

      storesData: null,
      employeesData: null,
      filteredEmployeeOptions: [],

      // Error states
      errors: {
        stores: null,
        employees: null,
        approvals: null,
      },
    };
  },
  created() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Phiếu yêu cầu duyệt chi', route: 'purchase-orders' },
      { title: 'Danh sách phiếu yêu cầu duyệt chi' },
    ]);

    this.listType = cloneDeep(APPROVAL_TYPE);
    this.listType.unshift({
      id: null,
      name: 'Tất cả loại duyệt chi',
    });

    this.initializeData();
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.filterComponent) {
        this.initializeFilterComponent();
      }
    });
  },

  computed: {
    /**
     * Check if any data is currently loading
     */
    isAnyLoading() {
      return Object.values(this.loadingStates).some((loading) => loading);
    },

    /**
     * Check if initialization is complete
     */
    isInitialized() {
      return (
        !this.loadingStates.initialization &&
        (this.storesData !== null || this.employeesData !== null)
      );
    },

    /**
     * Check if there are any errors
     */
    hasErrors() {
      return Object.values(this.errors).some((error) => error !== null);
    },

    /**
     * Get error summary for display
     */
    errorSummary() {
      const errors = Object.entries(this.errors)
        .filter(([, error]) => error !== null)
        .map(([key, error]) => `${key}: ${error.message || error}`);
      return errors.length > 0 ? errors.join(', ') : null;
    },
  },
  methods: {
    /**
     * Initialize data by fetching stores and employees concurrently
     * Implements caching, parallel loading, and proper error handling
     */
    async initializeData() {
      this.loadingStates.initialization = true;
      this.clearErrors();

      try {
        // Fetch stores and employees concurrently using Promise.allSettled
        this.loadingStates.stores = true;
        this.loadingStates.employees = true;

        const employeeRepository = RepositoryFactory.get('employees');

        const [storesResult, employeesResult, approvalsResult] =
          await Promise.allSettled([
            this.fetchStoreByUser(),
            employeeRepository.fetchListFilter(),
            this.fetchData(),
          ]);

        // Handle stores result (cửa hàng theo quyền user — cùng API @/view/mixins/api-common.mixins)
        if (storesResult.status === 'fulfilled') {
          this.storesData = storesResult.value.stores || [];
          this.errors.stores = null;
        } else {
          console.error('Error fetching stores:', storesResult.reason);
          this.errors.stores = storesResult.reason;
        }

        // Handle employees result
        if (employeesResult.status === 'fulfilled') {
          this.employeesData = employeesResult.value;
          this.errors.employees = null;
        } else {
          console.error('Error fetching employees:', employeesResult.reason);
          this.errors.employees = employeesResult.reason;
        }

        // Initialize filter component with data if both are available
        if (this.storesData && this.employeesData) {
          this.$nextTick(() => {
            this.initializeFilterComponent(this.employeesData, this.storesData);
          });
        }

        // Handle approvals result separately (already handled in fetchData)
        if (approvalsResult.status === 'rejected') {
          console.error('Error fetching approvals:', approvalsResult.reason);
          this.errors.approvals = approvalsResult.reason;
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        this.errors.initialization = error;
        makeToastFaile('Không thể tải dữ liệu ban đầu. Vui lòng thử lại.');
      } finally {
        this.loadingStates.initialization = false;
        this.loadingStates.stores = false;
        this.loadingStates.employees = false;
      }
    },

    /**
     * Fallback method for individual API calls if concurrent call fails
     */
    async handleInitializationFallback() {
      const employeeRepository = RepositoryFactory.get('employees');

      try {
        // Try to fetch employees first (more critical for filtering)
        const employees = await employeeRepository.fetchListFilter();
        this.employeesData = employees;
        this.errors.employees = null;

        // Initialize filter with employees data
        this.$nextTick(() => {
          if (this.$refs.filterComponent && employees) {
            this.$refs.filterComponent.setEmployees(employees);
          }
        });
      } catch (employeeError) {
        console.error('Error fetching employees:', employeeError);
        this.errors.employees = employeeError;
        makeToastFaile('Không thể tải danh sách nhân viên');
      }

      try {
        const { stores } = await this.fetchStoreByUser();
        this.storesData = stores || [];
        this.errors.stores = null;

        // Initialize filter with stores data
        this.$nextTick(() => {
          if (this.$refs.filterComponent && stores) {
            this.$refs.filterComponent.setStores(stores);
          }
        });
      } catch (storeError) {
        console.error('Error fetching stores:', storeError);
        this.errors.stores = storeError;
        makeToastFaile('Không thể tải danh sách cửa hàng');
      }
    },

    /**
     * Clear all error states
     */
    clearErrors() {
      this.errors = {
        stores: null,
        employees: null,
        approvals: null,
      };
    },

    /**
     * Initialize filter component with stores and employees data
     * This is called after the component is mounted
     */
    initializeFilterComponent(optionsEmployee, optionsStore) {
      if (optionsStore && this.$refs.filterComponent) {
        this.$refs.filterComponent.setStores(optionsStore);
      }
      if (optionsEmployee && this.$refs.filterComponent) {
        this.$refs.filterComponent.setEmployees(optionsEmployee);
      }
    },

    /**
     * Fetch approval data based on current filters
     * Uses repository pattern and improved error handling
     */
    async fetchData() {
      this.onLoading = true;
      this.loadingStates.approvals = true;
      this.errors.approvals = null;

      try {
        // Get current page from route
        this.page = this.$route.query.page || 1;

        // Get filter params
        const params = this.getFilterParams();

        // Fetch data from repository
        const approvalRepository = RepositoryFactory.get('approvals');
        const responseData = await approvalRepository.fetchApprovals(params);

        // Update component data
        this.totalPages = responseData.totalPage;
        this.totalItems = responseData.totalRow;
        this.listItem = responseData.dataset.map((item, index) => ({
          ...item,
          count: index + 1,
        }));
      } catch (error) {
        console.error('Error fetching approvals:', error);
        this.errors.approvals = error;

        // Provide more specific error messages
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Không thể tải danh sách phiếu duyệt chi';
        makeToastFaile(errorMessage);
      } finally {
        this.onLoading = false;
        this.loadingStates.approvals = false;
      }
    },

    /**
     * Get filter parameters for API request
     */
    getFilterParams() {
      let filterData = {};

      // Check if filter component exists before accessing its methods
      if (this.$refs.filterComponent) {
        filterData = this.$refs.filterComponent.formatFilterData();
      } else {
        // Fallback when filter component is not yet available
        filterData = {
          page: 1,
          limit: 10,
          code: this.initialFilterData.code || '',
          entityId: this.initialFilterData.entityId || '',
          status: this.initialFilterData.status,
          fromDate: this.initialFilterData.startDate
            ? moment(this.initialFilterData.startDate, 'DD/MM/YYYY').format(
                'yyyy-MM-DD',
              )
            : '',
          toDate: this.initialFilterData.endDate
            ? moment(this.initialFilterData.endDate, 'DD/MM/YYYY').format(
                'yyyy-MM-DD',
              )
            : '',
          storeId: this.initialFilterData.storeId,
          type: this.initialFilterData.type,
          paymentType: this.initialFilterData.paymentType,
          employeeId: this.initialFilterData.employeeId,
        };
      }

      // Add page and limit
      return {
        ...filterData,
        page: this.page,
        limit: 10,
      };
    },

    /**
     * Handle filter action from filter component
     */
    handleFilter(filterData) {
      // Reset to page 1 when filtering
      this.$route.query.page = 1;
      this.$router.push({
        name: 'list-approval',
      });

      // Update filter data and fetch
      this.initialFilterData = { ...filterData };
      this.fetchData();
    },

    /**
     * Handle page change from pagination component
     */
    handlePageChange(page) {
      this.page = page;
      this.fetchData();
    },

    /**
     * Export data to Excel
     */
    async exportToExcel() {
      try {
        const params = this.getFilterParams();
        await ApprovalService.exportToExcel(params);
      } catch (error) {
        console.error('Error exporting to Excel:', error);
      }
    },

    /**
     * Navigate to approval details/edit page
     */
    editItem(item) {
      this.$router.push({
        name: approvalRouter.info.name,
        params: { id: item.id },
      });
    },

    /**
     * Show approval modal
     */
    showApproveModal(approvalId) {
      this.$refs['popup-approve'].showModal(approvalId);
    },

    /**
     * Show change status modal
     */
    showChangeStatusModal(approvalModel) {
      this.$refs['approval-change-status'].showModal(approvalModel);
    },

    /**
     * Show warning for return request
     */
    showReturnWarning(item) {
      this.showWarningAlert(item, 'return');
    },

    /**
     * Show warning for delete
     */
    showDeleteWarning(item) {
      this.showWarningAlert(item, 'delete');
    },

    /**
     * Show warning alert for delete or return actions
     */
    showWarningAlert(item, action = 'delete') {
      const title =
        action === 'return'
          ? 'Hoàn duyệt yêu cầu duyệt chi!'
          : 'Xóa phiếu yêu cầu duyệt chi!';

      const text =
        action === 'return'
          ? 'Bạn có chắc muốn hoàn duyệt yêu cầu duyệt duyệt chi này?'
          : 'Bạn có chắc muốn xóa phiếu yêu cầu duyệt chi này?';

      const confirmText = action === 'return' ? 'Đồng ý' : 'Xóa';

      Swal.fire({
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: confirmText,
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        position: 'top',
      }).then((result) => {
        if (result.value) {
          action === 'return'
            ? this.returnApproval(item)
            : this.deleteApproval(item);
        }
      });
    },

    /**
     * Return an approval
     */
    async returnApproval(item) {
      try {
        const response = await ApprovalService.returnApproval(item.id);
        makeToastSuccess(response.message);
        this.fetchData();
      } catch (error) {
        console.error('Error returning approval:', error);
        const message = error.response?.data?.message || 'Lỗi!';
        makeToastFaile(message);
      }
    },

    /**
     * Delete an approval
     */
    async deleteApproval(item) {
      try {
        const response = await ApprovalService.deleteApproval(item.id);
        if (response.status === 200) {
          makeToastSuccess(response.message);
          this.fetchData();
        } else {
          makeToastFaile(response.message);
        }
      } catch (error) {
        console.error('Error deleting approval:', error);
        makeToastFaile('Không thể xóa phiếu duyệt chi');
      }
    },

    /**
     * Open file modal for a specific approval
     */
    openFileModal(item) {
      this.entityUploadId = item.id;
      this.$refs['file-modal'].show();
    },
  },
};
</script>

<style lang="scss">
.list-approval-page {
  .dropdown-item-text {
    font-size: 12px;
    color: #3f4254;
  }

  .dropdown-item {
    padding: 0px;
  }

  .search-col {
    display: flex;
    align-items: center;
  }
}
</style>
